import { Button, Card, Empty, notification } from 'antd';
import { OverViewCardInfo } from '../OverviewCardInfo';
import { useEffect, useState } from 'react';
import { CustomModal } from '@/components/CustomModal';
import { QueryObserverResult, RefetchOptions, UseMutationResult } from '@tanstack/react-query';
import { MODAL_IDS } from 'src/enums/EModal';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { openModal } from '@/store/modal/slice';
import './EmailWorkflow.scss';
import { deepEqual, safeJSONParse } from '@/utils/helper';
import { User } from '@/types/TAccounts';
import { TUTORIALS } from 'src/enums/ETutorials';
import { FeatureFlaggedWorkflowAvailability } from '../WorkflowAvailability/FeatureFlaggedWorkflowAvailability';
import { TWorkflowAvailabilities } from '@/types/TWorkflowAvailabilities';
import { DeleteOutlined, EditOutlined, MailOutlined } from '@ant-design/icons';
import EmailWorkflowForm from './EmailWorkflowForm';
import { ChevronDown, ChevronUp, FileText, Mail, Users } from 'lucide-react';
import { getLoggedInUser } from '@/store/account/selector';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { decrementScenarioCount } from '@/store/scenarioCounts/slice';
import { FormActionType, WorkflowType } from 'src/enums/EWorkflow';
import ConfirmDeleteModal from '@/components/ConfirmDeleteModal/ConfirmDeleteModal';

interface EmailWorkflowInfoProps {
	data: any;
	refetch: (options?: RefetchOptions) => Promise<QueryObserverResult<User, Error>>;
	mutation: UseMutationResult<unknown, Error, any, unknown>;
	debrand: boolean;
	isNoviUser: boolean;
}

export const EmailWorkflowInfo: React.FC<EmailWorkflowInfoProps> = ({
	data,
	refetch,
	mutation,
	debrand,
	isNoviUser,
}) => {
	const dispatch = useAppDispatch();
	const user = useAppSelector(getLoggedInUser);
	const [emailWorkflowData, setEmailWorkflowData] = useState<any[]>([]);
	const [scenarioIndex, setScenarioIndex] = useState<number>();
	const [formInitialValues, setFormInitialValues] = useState<any>(null);
	const [isExpanded, setIsExpanded] = useState(false);
	const [expandedScenario, setExpandedScenario] = useState<number | null>(null);
	const [updatingIndex, setUpdatingIndex] = useState<number | null>(null);
	// Check if Mailgun is configured
	const isMailgunConfigured = !!(user?.mailgunCreds?.domain && user?.mailgunCreds?.apiKey);

	useEffect(() => {
		if (data?.emailWorkflows) {
			const res = safeJSONParse(data.emailWorkflows, []);
			setEmailWorkflowData(res);
		}
	}, [data?.emailWorkflows]);

	useEffect(() => {
		const previousWorkflowData = formInitialValues?.emailWorkflows?.[0]?.previousWorkflowData;

		if (!deepEqual(previousWorkflowData, emailWorkflowData)) {
			setFormInitialValues({
				emailWorkflows: [
					{
						previousWorkflowData: emailWorkflowData,
					},
				],
			});
		}
	}, [emailWorkflowData, formInitialValues]);

	const handleEditScenario = (scenario: any, index: number) => {
		const initialValues = {
			emailWorkflows: [
				{
					description: scenario.description,
					emailSubject: scenario.emailSubject,
					emailBody: scenario.emailBody,
					emailTo: scenario.emailTo || '',
					ccEmails: scenario.ccEmails || '',
					index,
					previousWorkflowData: emailWorkflowData,
					askForPermissionOnEmail: scenario.askForPermissionOnEmail,
					configEnabled: scenario?.configEnabled ?? false,
					fallbackMessage: scenario?.fallbackMessage || '',
					availability: scenario?.availability || {},
				},
			],
		};

		setFormInitialValues(initialValues);
		setScenarioIndex(index);
		dispatch(openModal({ modalId: MODAL_IDS.EMAIL_WORKFLOW_EDIT_MODAL }));
	};

	const handleDeleteScenario = async (index: number) => {
		try {
			await mutation.mutateAsync({
				emailWorkflows: emailWorkflowData?.filter((_, i) => i !== index),
				deleteIndex: index,
			});

			dispatch(decrementScenarioCount(WorkflowType.EMAIL));

			notification.success({ message: `Email scenario ${index + 1} deleted successfully.` });
			refetch();
		} catch {
			notification.error({ message: 'Something went wrong, please try again later!' });
		}
	};

	const toggleExpand = () => {
		setIsExpanded(!isExpanded);
	};

	const toggleExpandScenario = (index: number) => {
		setExpandedScenario(expandedScenario === index ? null : index);
	};

	const MailgunConfigurationMessage = () => {
		return (
			<div className="mailgun-config-message">
				<Card className="mt-4">
					<div className="flex flex-col items-center justify-center py-6 text-center">
						<div className="bg-blue-50 p-4 rounded-full mb-4">
							<MailOutlined style={{ fontSize: '36px', color: '#1890ff' }} />
						</div>
						<h2 className="text-xl font-semibold mb-2">Configure Mailgun Integration</h2>
						<p className="text-gray-500 mb-4 max-w-lg">
							To use email workflows, you need to set up your Mailgun account credentials. This will
							enable you to create and manage automated email scenarios.
						</p>
					</div>
				</Card>
			</div>
		);
	};

	const handleSetWorkflowAvailability = async (
		data: TWorkflowAvailabilities,
		index: number,
		fallbackMessage: string,
		configEnabled: boolean,
	) => {
		try {
			setUpdatingIndex(index);
			const currentWorkflows = emailWorkflowData || [];
			currentWorkflows[index].availability = data;
			currentWorkflows[index].configEnabled = configEnabled;
			currentWorkflows[index].fallbackMessage = fallbackMessage;
			await mutation.mutateAsync({ emailWorkflows: currentWorkflows });
			await refetch();
			notification.success({
				message: 'Email workflow availability has been updated!',
				duration: 3,
			});
		} catch (error: any) {
			notification.error({
				message: 'Error updating availability',
				description: error.toString(),
			});
		} finally {
			setUpdatingIndex(null);
		}
	};
	const InfoComponent = () => {
		if (!isMailgunConfigured) {
			return <MailgunConfigurationMessage />;
		}
		const handleConfirmDelete = (index: number) => {
			ConfirmDeleteModal({
				itemName: `Email Scenario ${index + 1}`,
				onConfirm: () => handleDeleteScenario(index),
			});
		};
		return (
			<>
				{emailWorkflowData && emailWorkflowData.length > 0 ? (
					emailWorkflowData.map((emailWorkflow, index) => (
						<Card
							key={index + 1}
							className="mt-4 email-workflow-card"
							title={
								<div className="flex justify-between items-center w-full">
									<div className="flex items-center">
										<MailOutlined className="mr-2" />
										<span
											className="font-bold text-base cursor-pointer"
											onClick={() => toggleExpandScenario(index)}
										>
											{`Email Scenario ${index + 1}`}
										</span>
									</div>
									<div className="flex items-center">
										<Button
											type="primary"
											className="mr-2"
											icon={<EditOutlined />}
											onClick={() => handleEditScenario(emailWorkflow, index)}
											data-testid={`edit-email-button-${index}`}
										>
											Edit
										</Button>
										<Button
											loading={mutation.isPending && mutation.variables?.deleteIndex === index}
											type="primary"
											danger
											icon={<DeleteOutlined />}
											onClick={() => handleConfirmDelete(index)}
											data-testid={`delete-email-button-${index}`}
										>
											Delete
										</Button>

										<FeatureFlaggedWorkflowAvailability
											availability={emailWorkflow?.availability}
											initialConfigEnabled={emailWorkflow?.configEnabled ?? false}
											initialFallbackMessage={emailWorkflow?.fallbackMessage}
											setWorkflowAvailability={handleSetWorkflowAvailability}
											index={index}
											isLoading={updatingIndex === index}
										/>
									</div>
								</div>
							}
						>
							<div className="mb-4 border rounded-lg shadow-sm overflow-hidden bg-white">
								{/* Header - Always visible */}
								<div
									className="flex justify-between items-center p-4 cursor-pointer bg-gradient-to-r from-blue-50 to-white"
									onClick={toggleExpand}
								>
									<div className="flex items-center space-x-3">
										<div className="bg-blue-100 p-2 rounded-full">
											<Mail size={20} className="text-blue-600" />
										</div>
										<div>
											<h3 className="font-medium text-gray-800">
												{emailWorkflow.emailSubject || 'Email Workflow'}
											</h3>
											<p className="text-sm text-gray-600 truncate max-w-md">
												{emailWorkflow.description?.slice(0, 60)}
												{emailWorkflow.description?.length > 60 ? '...' : ''}
											</p>
										</div>
									</div>
									<div className="flex items-center">
										<span className="text-sm text-blue-600 mr-2">
											{isExpanded ? 'Hide Details' : 'View Details'}
										</span>
										{isExpanded ? (
											<ChevronUp className="text-blue-600" size={20} />
										) : (
											<ChevronDown className="text-blue-600" size={20} />
										)}
									</div>
								</div>

								{/* Expanded Content */}
								{isExpanded && (
									<div className="border-t border-gray-100">
										<div className="p-4 space-y-4">
											{/* Description */}
											<div className="pb-3 border-b border-gray-100">
												<h4 className="text-sm font-medium text-gray-500 mb-2 flex items-center">
													<FileText size={16} className="mr-2" />
													Scenario Description
												</h4>
												<div className="pl-6 text-gray-700 whitespace-pre-wrap">
													{emailWorkflow.description}
												</div>
											</div>

											{/* Email To */}
											<div className="pb-3 border-b border-gray-100">
												<h4 className="text-sm font-medium text-gray-500 mb-2 flex items-center">
													<Users size={16} className="mr-2" />
													Email To
												</h4>
												<div className="pl-6 bg-gray-50 p-2 rounded text-gray-700 font-mono text-sm">
													{emailWorkflow.emailTo}
												</div>
											</div>

											{/* Email Subject */}
											<div className="pb-3 border-b border-gray-100">
												<h4 className="text-sm font-medium text-gray-500 mb-2 flex items-center">
													<Mail size={16} className="mr-2" />
													Email Subject
												</h4>
												<div className="pl-6 bg-gray-50 p-2 rounded text-gray-700 font-mono text-sm">
													{emailWorkflow.emailSubject}
												</div>
											</div>

											{/* Email Body */}
											<div className="pb-3 border-b border-gray-100">
												<h4 className="text-sm font-medium text-gray-500 mb-2 flex items-center">
													<FileText size={16} className="mr-2" />
													Email Body
												</h4>
												<div className="pl-6">
													<div className="bg-gray-50 p-3 rounded border border-gray-200 whitespace-pre-wrap text-gray-700">
														{emailWorkflow.emailBody}
													</div>
												</div>
											</div>

											{/* CC Recipients if available */}
											{emailWorkflow.ccEmails && (
												<div className="pb-3 border-b border-gray-100">
													<h4 className="text-sm font-medium text-gray-500 mb-2 flex items-center">
														<Users size={16} className="mr-2" />
														CC Recipients
													</h4>
													<div className="pl-6 bg-gray-50 p-2 rounded text-gray-700 font-mono text-sm">
														{emailWorkflow.ccEmails}
													</div>
												</div>
											)}

											{/* Ask for Permission */}
											<div>
												<h4 className="text-sm font-medium text-gray-500 mb-2 flex items-center">
													<FileText size={16} className="mr-2" />
													Ask for Permission
												</h4>
												<div className="pl-6 flex items-center">
													<div
														className={`h-4 w-4 rounded-full mr-2 ${emailWorkflow.askForPermissionOnEmail ? 'bg-green-500' : 'bg-red-500'}`}
													/>
													<span className="text-gray-700">
														{emailWorkflow.askForPermissionOnEmail ? 'Yes' : 'No'}
													</span>
												</div>
											</div>
										</div>
									</div>
								)}
							</div>
						</Card>
					))
				) : (
					<Empty
						description="No email workflows defined yet"
						image={Empty.PRESENTED_IMAGE_SIMPLE}
					/>
				)}
			</>
		);
	};

	const baseClass = 'email-workflow';
	return (
		<div className={baseClass}>
			<OverViewCardInfo
				width={'50%'}
				formActionType="Add"
				form={
					isMailgunConfigured ? (
						<EmailWorkflowForm
							mutation={mutation}
							initialValues={formInitialValues}
							refetch={refetch}
							actionType={FormActionType.ADD}
							debrand={debrand}
							isNoviUser={isNoviUser}
						/>
					) : null
				}
				formTitle="Add Email Workflow"
				info={<InfoComponent />}
				footer={null}
				modalId={MODAL_IDS.EMAIL_WORKFLOW_MODAL}
				tutorialId={TUTORIALS.EMAIL_WORKFLOW}
				displayTutorialActionBtn={!debrand && isMailgunConfigured}
			/>

			{isMailgunConfigured && (
				<CustomModal
					width={'50%'}
					title={<span className="font-bold">Edit Email Scenario</span>}
					children={
						<EmailWorkflowForm
							actionType={FormActionType.EDIT}
							showAddFieldButton={false}
							initialValues={formInitialValues}
							refetch={refetch}
							mutation={mutation}
							scenarioIndex={scenarioIndex}
							debrand={debrand}
							isNoviUser={isNoviUser}
						/>
					}
					modalId={MODAL_IDS.EMAIL_WORKFLOW_EDIT_MODAL}
					footer={null}
				/>
			)}
		</div>
	);
};
